import Cookie from 'js-cookie';
import { useCallback, useEffect } from 'react';
import { useResetRecoilState } from 'recoil';
import { authenticationState } from 'state/atoms';
import { LOGGED_IN_JWT_COOKIE } from 'utils/constants';
import { getLoggedInJwtCookie, parseJwt } from './../../connectors/storage';

function isValidJwt(jwt) {
  const expirationTime = new Date(jwt.exp * 1000);
  const currentTime = new Date();

  return jwt && jwt.exp && jwt.readyToExecuteAtMs && currentTime < expirationTime;
}

const removeAuthCookie = () => {
  Cookie.remove(LOGGED_IN_JWT_COOKIE);
};

export const useSessionController = () => {
  const resetAuthentication = useResetRecoilState(authenticationState);

  const handleCookieValidity = useCallback(() => {
    const jwt = Cookie.get(LOGGED_IN_JWT_COOKIE);

    if (!jwt) {
      console.error('Session expired or invalid cookies');

      resetAuthentication();
    }
  }, [resetAuthentication]);

  const handleUserActivity = useCallback(async () => {
    const loggedInUserEcommJWT = getLoggedInJwtCookie();

    let readyToExecuteAtMs = 0,
      exp = 0;

    if (loggedInUserEcommJWT) {
      const jwt = parseJwt(loggedInUserEcommJWT);

      if (!isValidJwt(jwt)) {
        console.error('Invalid JWT token');
        resetAuthentication();

        return;
      }

      readyToExecuteAtMs = jwt.readyToExecuteAtMs;
      exp = jwt.exp * 1000;
    }

    const refreshThreshold = new Date(readyToExecuteAtMs);
    const currentTime = new Date();
    const expiryTime = new Date(exp);

    if (process.env.REACT_APP_ENV !== 'prod') {
      console.log('Refresh threshold time:', refreshThreshold);
      console.log('Current time:', currentTime);
    }

    if (loggedInUserEcommJWT && currentTime > refreshThreshold && currentTime < expiryTime) {
      process.env.REACT_APP_ENV !== 'prod' && console.log('User activity detected, refreshing session token...');
      const response = await fetch(
        `${process.env.REACT_APP_CALIX_PORTAL_URL}/bin/calix/servlets/refreshsessiontoken`,
        {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify({}),
        }
      );

      if (!response.status.toString().startsWith('2')) {
        console.error('Failed to refresh session token');
        if (response.status === 401) {
          removeAuthCookie();
          resetAuthentication();

          return;
        }

        handleCookieValidity();
      } else {
        process.env.REACT_APP_ENV !== 'prod' && console.log('Session token refreshed successfully');
      }

      process.env.REACT_APP_ENV !== 'prod' && console.log(response);
    }

    handleCookieValidity();
  }, [handleCookieValidity, resetAuthentication]);

  // Attach events to listen for user activity
  useEffect(() => {
    document.addEventListener('visibilitychange', handleUserActivity);
    document.addEventListener('mousedown', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);

    handleUserActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
